/**
 * Famous numbers — the lore Logan wrote for the prototype, lightly fact-checked.
 * Reaching one for the first time reveals its card in the Collection and pays a bonus.
 */

export type FamousCategory = 'maths' | 'culture' | 'science' | 'history';

export interface FamousNumber {
  id: string;
  n: number;
  category: FamousCategory;
  name: string;
  desc: string;
}

export const CATEGORY_LABELS: Readonly<Record<FamousCategory, string>> = {
  maths: 'Mathematics',
  culture: 'Pop culture',
  science: 'Science',
  history: 'History',
};

export const FAMOUS_NUMBERS: readonly FamousNumber[] = [
  // Mathematics
  {
    id: 'm-0',
    n: 0,
    category: 'maths',
    name: 'The Origin',
    desc: 'The additive identity, and the border between negative and positive.',
  },
  {
    id: 'm-1',
    n: 1,
    category: 'maths',
    name: 'Unity',
    desc: 'The multiplicative identity. Every whole number is built from it.',
  },
  {
    id: 'm-4',
    n: 4,
    category: 'maths',
    name: 'The First Square',
    desc: 'The smallest composite number, and 2².',
  },
  {
    id: 'm-6',
    n: 6,
    category: 'maths',
    name: 'The First Perfect Number',
    desc: 'Equal to the sum of its proper divisors: 1 + 2 + 3.',
  },
  {
    id: 'm-8',
    n: 8,
    category: 'maths',
    name: 'The First Cube',
    desc: 'The first cube after 1: 2³.',
  },
  {
    id: 'm-12',
    n: 12,
    category: 'maths',
    name: 'The Dozen',
    desc: 'A superior highly composite number with six divisors.',
  },
  {
    id: 'm-28',
    n: 28,
    category: 'maths',
    name: 'The Second Perfect Number',
    desc: '1 + 2 + 4 + 7 + 14 = 28.',
  },
  {
    id: 'm-144',
    n: 144,
    category: 'maths',
    name: 'The Gross',
    desc: 'A dozen dozen, and the 12th Fibonacci number.',
  },
  {
    id: 'm-256',
    n: 256,
    category: 'maths',
    name: 'One Byte',
    desc: 'The number of values a single byte can hold: 2⁸.',
  },
  {
    id: 'm-360',
    n: 360,
    category: 'maths',
    name: 'Degrees in a Circle',
    desc: 'A highly composite division of the circle inherited from Babylon.',
  },
  {
    id: 'm-496',
    n: 496,
    category: 'maths',
    name: 'The Third Perfect Number',
    desc: 'The sum of its proper divisors, like 6 and 28 before it.',
  },
  {
    id: 'm-1729',
    n: 1729,
    category: 'maths',
    name: 'The Taxicab Number',
    desc: 'The smallest sum of two cubes in two ways: 1³ + 12³ = 9³ + 10³.',
  },
  {
    id: 'm-6174',
    n: 6174,
    category: 'maths',
    name: "Kaprekar's Constant",
    desc: 'Sort any 4-digit number’s digits, subtract, repeat — you land here.',
  },
  {
    id: 'm-8128',
    n: 8128,
    category: 'maths',
    name: 'The Fourth Perfect Number',
    desc: 'The last perfect number the ancient Greeks knew.',
  },
  {
    id: 'm-65536',
    n: 65536,
    category: 'maths',
    name: 'Two to the Sixteenth',
    desc: 'The number of values in 16 bits, and 2^2^2^2.',
  },
  {
    id: 'm-neg1',
    n: -1,
    category: 'maths',
    name: 'The Negative Unit',
    desc: 'The additive inverse of 1, and the square of i.',
  },
  {
    id: 's-neg40',
    n: -40,
    category: 'science',
    name: 'Where Scales Agree',
    desc: '−40 °C is exactly −40 °F.',
  },
  {
    id: 's-neg273',
    n: -273,
    category: 'science',
    name: 'Absolute Zero',
    desc: '0 kelvin is −273.15 °C, the coldest anything can be.',
  },

  // Pop culture
  {
    id: 'c-7',
    n: 7,
    category: 'culture',
    name: 'Lucky Seven',
    desc: 'The most popular “favorite number” across cultures, and 007’s license.',
  },
  {
    id: 'c-42',
    n: 42,
    category: 'culture',
    name: 'The Answer',
    desc: 'The answer to life, the universe and everything.',
  },
  {
    id: 'c-64',
    n: 64,
    category: 'culture',
    name: 'A Full Stack',
    desc: 'A full Minecraft stack, and the squares on a chessboard.',
  },
  {
    id: 'c-73',
    n: 73,
    category: 'culture',
    name: 'The Sheldon Prime',
    desc: 'The 21st prime, and 7 × 3 = 21. Its mirror 37 is the 12th prime.',
  },
  {
    id: 'c-108',
    n: 108,
    category: 'culture',
    name: 'The Numbers',
    desc: '4 + 8 + 15 + 16 + 23 + 42 = 108, the countdown in Lost.',
  },
  {
    id: 'c-404',
    n: 404,
    category: 'culture',
    name: 'Not Found',
    desc: 'The web’s most famous error code.',
  },
  {
    id: 'c-1337',
    n: 1337,
    category: 'culture',
    name: 'Leet',
    desc: 'Early hacker slang for “elite”.',
  },
  {
    id: 'c-9001',
    n: 9001,
    category: 'culture',
    name: "It's Over 9000!",
    desc: 'Vegeta’s scouter reading, and an internet legend.',
  },

  // Science
  {
    id: 's-1',
    n: 1,
    category: 'science',
    name: 'Hydrogen',
    desc: 'Element 1. The most abundant element in the universe.',
  },
  {
    id: 's-6',
    n: 6,
    category: 'science',
    name: 'Carbon',
    desc: 'Element 6. The backbone of all known life.',
  },
  {
    id: 's-8',
    n: 8,
    category: 'science',
    name: 'Oxygen',
    desc: 'Element 8. What you are breathing right now.',
  },
  {
    id: 's-14',
    n: 14,
    category: 'science',
    name: 'Silicon',
    desc: 'Element 14. The semiconductor inside every chip.',
  },
  {
    id: 's-26',
    n: 26,
    category: 'science',
    name: 'Iron',
    desc: 'Element 26. Where fusion in a star’s core stops paying off.',
  },
  {
    id: 's-79',
    n: 79,
    category: 'science',
    name: 'Gold',
    desc: 'Element 79. Much of it forged in neutron-star collisions.',
  },
  {
    id: 's-92',
    n: 92,
    category: 'science',
    name: 'Uranium',
    desc: 'Element 92. The heaviest element found in quantity in nature.',
  },
  {
    id: 's-100',
    n: 100,
    category: 'science',
    name: 'Boiling Point',
    desc: 'Water boils at 100 °C at sea level.',
  },

  // History
  {
    id: 'h-1066',
    n: 1066,
    category: 'history',
    name: 'Battle of Hastings',
    desc: 'The Norman conquest of England begins.',
  },
  {
    id: 'h-1215',
    n: 1215,
    category: 'history',
    name: 'Magna Carta',
    desc: 'The charter that put the king under the law.',
  },
  {
    id: 'h-1440',
    n: 1440,
    category: 'history',
    name: 'The Printing Press',
    desc: 'Around this year Gutenberg’s press begins to change everything.',
  },
  {
    id: 'h-1492',
    n: 1492,
    category: 'history',
    name: 'Columbus Sails',
    desc: 'Columbus reaches the Caribbean.',
  },
  {
    id: 'h-1776',
    n: 1776,
    category: 'history',
    name: 'Independence',
    desc: 'The US Declaration of Independence is signed.',
  },
  {
    id: 'h-1969',
    n: 1969,
    category: 'history',
    name: 'Moon Landing',
    desc: 'Apollo 11 puts the first people on the Moon.',
  },
  {
    id: 'h-2000',
    n: 2000,
    category: 'history',
    name: 'Y2K',
    desc: 'The year computers were feared to forget how to count.',
  },
];

const BY_NUMBER = new Map<number, FamousNumber[]>();
for (const f of FAMOUS_NUMBERS) {
  const list = BY_NUMBER.get(f.n);
  if (list) list.push(f);
  else BY_NUMBER.set(f.n, [f]);
}

const NONE: readonly FamousNumber[] = [];

export function famousAt(n: number): readonly FamousNumber[] {
  return BY_NUMBER.get(n) ?? NONE;
}
